import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { departmentService } from '../../services/departmentService';
import AuthLayout from './AuthLayout';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { 
  SECTORS,
  DEFAULT_USER_DATA
} from '../../constants/authConstants';
import { validateSignUpForm } from '../../utils/authValidation';

const SignUp = () => {
  const [formData, setFormData] = useState(DEFAULT_USER_DATA);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [signupResult, setSignupResult] = useState(null);
  
  const [allDepartments, setAllDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  
  const { signUp, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    if (formData.sector) {
      const depts = allDepartments.filter(d => 
        d.sector === formData.sector && d.isActive
      );
      setFilteredDepartments(depts);
    } else {
      setFilteredDepartments([]);
    }
  }, [formData.sector, allDepartments]);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const departments = await departmentService.getActiveDepartments();
      setAllDepartments(departments);
    } catch (error) {
      console.error('Error loading departments:', error);
      setAllDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    if (authError) {
      clearError();
    }

    if (field === 'sector') {
      setFormData(prev => ({
        ...prev,
        department: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateSignUpForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        fullName: formData.fullName.trim(),
        sector: formData.sector,
        department: formData.department
      };
      
      const result = await signUp(formData.email, formData.password, userData);
      console.log('Sign up successful, showing success message');
      
      setSignupResult(result);
      setShowSuccess(true);
    } catch (error) {
      console.error('Sign up failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentOptions = () => {
    if (!formData.sector) {
      return [{ value: '', label: 'Select a sector first' }];
    }
    
    if (loadingDepartments) {
      return [{ value: '', label: 'Loading departments...' }];
    }
    
    if (filteredDepartments.length === 0) {
      return [{ value: '', label: 'No departments available' }];
    }
    
    return filteredDepartments
      .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      .map(dept => ({
        value: dept.departmentCode,
        label: dept.departmentName
      }));
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="card p-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-12 h-12 bg-success-subtle rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-text-primary">Registration Successful!</h2>
                
                <div className="bg-info-subtle border border-border-primary rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="w-4 h-4 text-info mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-info">
                      <p className="font-medium mb-1">🎉 Welcome to StateSync!</p>
                      <div className="space-y-1 text-xs">
                        <p>• Account status: <strong>Pending Admin Approval</strong></p>
                        <p>• You'll receive an email notification once approved</p>
                        <p>• Approval typically takes 1-2 business days</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-text-secondary text-sm">
                  Account registered for <strong>{formData.fullName}</strong>
                </p>
              </div>

              <div className="space-y-3 pt-3">
                <Link to="/login">
                  <Button className="w-full" size="md">
                    Go to Login Page
                  </Button>
                </Link>
                
                <div className="text-xs text-text-tertiary">
                  <div className="bg-surface-secondary rounded p-2 text-left">
                    <p className="font-medium mb-1">What happens next?</p>
                    <p>1. Admin reviews your application</p>
                    <p>2. You receive approval email</p>
                    <p>3. Login with your credentials</p>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    setSignupResult(null);
                    setFormData(DEFAULT_USER_DATA);
                    setErrors({});
                  }}
                  className="text-sm text-text-link hover:text-text-link-hover transition-colors"
                >
                  Register Another Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout 
      title="Create Account" 
      subtitle="Join StateSync Government Portal"
      wide={true}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Full Name */}
        <Input
          label="Full Name"
          type="text"
          value={formData.fullName}
          onChange={(e) => handleInputChange('fullName', e.target.value)}
          placeholder="Enter your full name"
          error={errors.fullName}
          required
          autoComplete="name"
          className="text-sm"
        />

        {/* Sector and Department - Side by Side */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Sector"
            value={formData.sector}
            onChange={(e) => handleInputChange('sector', e.target.value)}
            options={[
              { value: '', label: 'Select a sector' },
              ...SECTORS
            ]}
            error={errors.sector}
            required
            className="text-sm"
          />

          <Select
            label="Department"
            value={formData.department}
            onChange={(e) => handleInputChange('department', e.target.value)}
            options={getDepartmentOptions()}
            error={errors.department}
            required
            disabled={!formData.sector || loadingDepartments}
            className="text-sm"
          />
        </div>

        {/* Email - Full Width */}
        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="Enter your official email address"
          error={errors.email}
          required
          autoComplete="email"
          className="text-sm"
        />

        {/* Password Fields - Side by Side */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Enter your password"
            error={errors.password}
            required
            autoComplete="new-password"
            className="text-sm"
          />
          
          <Input
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            placeholder="Confirm your password"
            error={errors.confirmPassword}
            required
            autoComplete="new-password"
            className="text-sm"
          />
        </div>

        {/* Auth Error */}
        {authError && (
          <div className="bg-error-subtle border border-border-error rounded-lg p-3">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-error">{authError}</span>
            </div>
          </div>
        )}

        {/* Terms Notice - Compact */}
        <div className="bg-surface-secondary border border-border-primary rounded-lg p-2">
          <div className="flex items-center">
            <svg className="w-3 h-3 text-text-tertiary mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-xs text-text-secondary">
              <span className="font-medium">Account Approval Required:</span> Admin will review and activate your account within 1-2 business days.
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          loading={loading || loadingDepartments}
          disabled={loading || loadingDepartments}
          className="w-full"
          size="md"
        >
          {loading ? 'Creating Account...' : loadingDepartments ? 'Loading Departments...' : 'Create Account'}
        </Button>

        {/* Sign In Link */}
        <div className="text-center pt-3 border-t border-border-primary">
          <p className="text-sm text-text-secondary">
            Already have an account?{' '}
            <Link 
              to="/login"
              className="text-text-link hover:text-text-link-hover font-medium transition-colors"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default SignUp;